import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr } from "../../lib/api";
import { PageHeader, btnPrimary, btnTurq, EmptyState } from "../../components/bits";
import { Plus, BellRing } from "lucide-react";
import { CoordinatorsTable } from "../../components/admin/CoordinatorsTable";
import { CoordinatorFormModal } from "../../components/admin/CoordinatorFormModal";

const emptyCoord = { name: "", email: "", password: "", school_id: "", username: "" };

export default function AdminCoordinators() {
  const [coords, setCoords] = useState(null);
  const [schools, setSchools] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyCoord);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/coordinators").then((r) => setCoords(r.data)).catch((e) => toast.error(fmtErr(e))), []);
  useEffect(() => {
    load();
    api.get("/admin/schools").then((r) => setSchools(r.data.map((s) => ({ id: s.school.id, name: s.school.name })))).catch(() => {});
  }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      if (modal?.mode === "edit") {
        await api.put(`/admin/coordinators/${modal.coord.id}`, form);
        toast.success("تم تحديث حساب المنسق");
      } else {
        await api.post("/admin/coordinators", form);
        toast.success("تم إنشاء حساب المنسق");
      }
      setModal(null);
      setForm(emptyCoord);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (c) => {
    setForm({ name: c.name, email: c.email, password: "", school_id: c.school_id || "", username: c.username || "" });
    setModal({ mode: "edit", coord: c });
  };

  const remove = async (c) => {
    if (!window.confirm(`حذف حساب المنسق «${c.name}»؟`)) return;
    try {
      await api.delete(`/admin/coordinators/${c.id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const remindPledges = async () => {
    try {
      const { data } = await api.post("/admin/pledges/remind");
      toast.success(`تم إرسال التنبيه إلى ${data.reminded} منسقاً غير موثّق`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (!coords) return <div className="text-center text-mutext py-20 font-head">جارٍ التحميل …</div>;

  return (
    <div data-testid="admin-coordinators-page">
      <PageHeader title="المنسقون" sub="مرتّبون تنازلياً حسب الأداء: نسبة المشاركة + الفرص النوعية + الانتظام + التفاعل مع التحديات">
        <button data-testid="coordinator-add-btn" onClick={() => { setForm(emptyCoord); setModal({ mode: "add" }); }} className={btnPrimary}>
          <Plus className="w-4 h-4" /> إنشاء حساب منسق
        </button>
        <button data-testid="pledge-remind-btn" onClick={remindPledges} className={btnTurq}>
          <BellRing className="w-4 h-4" /> تنبيه غير الموثّقين للتعهد
        </button>
      </PageHeader>

      {coords.length === 0 ? (
        <EmptyState testid="coordinators-empty" text="لا يوجد منسقون بعد" />
      ) : (
        <CoordinatorsTable coords={coords} onEdit={openEdit} onRemove={remove} />
      )}

      <CoordinatorFormModal open={!!modal} mode={modal?.mode} onClose={() => setModal(null)}
        form={form} setForm={setForm} schools={schools} busy={busy} onSave={save} />
    </div>
  );
}
